import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import ReactTooltip from 'react-tooltip'
import { useTranslation } from 'react-i18next'

import api from '../../../../api/api'
import Stepper from '../../../Stepper/Stepper'
import Icon from '../../../Icons'
import ModalContainer from '../../../ModalContainer/ModelContainer'
import TextField from '../../../FormLegacy/FormFieldsV2/TextField'
import Filter from '../../../Filter/Filter'
import ToggleField from '../../../FormLegacy/FormFieldsV2/ToggleField'
import RadioField from '../../../FormLegacy/FormFieldsV2/RadioField'
import DateField from '../../../FormLegacy/FormFieldsV2/DateField'
import CheckboxField from '../../../FormLegacy/FormFieldsV2/CheckboxField'
import {
  selectBuildingsForFilter,
  fetchSingleBuilding,
} from '../../../../reducers/buildingSelectorReducer'
import { getCheckboxHandler, getNamedHandler } from '../helpers'
import { cutTooLong } from '../../../../helpers/helpers'
import { useSnackbar } from '../../../../helpers/hooks/useSnackbar'
import EditorText from '../../../EditorText/EditorText'

import RewardsAdvanced from './components/Steps/RewardsAdvanced'
import RewardsParameters from './components/Steps/RewardsParameters'
import RewardsActivityDetails from './components/Steps/RewardsActivityDetails'

const MAX_AMOUNT_VALUE = 5000
const POINTS_MULTIPLIER = 100
const USAGE_LIMIT_TIMES = 1

const RewardsAddLoyaltyActivityModal = ({
  onClose = () => {},
  fetchSingleBuilding = () => {},
  building,
  buildings,
  client,
}) => {
  const { t } = useTranslation(['rewards-loyalty', 'common'])
  const isMultipleBuildings = buildings.length > 1
  const [currentStep, setCurrentStep] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [formState, setFormState] = useState({
    building: building ? building.id : null,
    activityName: '',
    description: '',
    amount: null,
    requestApproval: false,
    featured: false,
    multipleBuildings: isMultipleBuildings,
    buildingIds: [],
    verificationType: 'none',
    expDate: null,
    notifyResidents: false,
    notifyResident: false,
    isFeatured: false,
  })
  const { displaySnackbar } = useSnackbar()

  const onSubmitFinally = async () => {
    if (building.id) {
      await fetchSingleBuilding(building.id)
    }
    setIsLoading(false)
    onClose()
  }

  const onSubmit = async () => {
    try {
      setIsLoading(true)

      const requestData = {
        name: formState.activityName,
        description: formState.description,
        points: formState.amount * POINTS_MULTIPLIER,
        verificationMethod: formState.verificationType,
        expirationDate: formState.expDate ? formState.expDate.valueOf() : undefined,
        requiresApproval: formState.requestApproval,
      }

      if (client) {
        requestData.clientId = client.clientId || client.id
        requestData.notifyResident = formState.notifyResident
        requestData.usageLimitTimes = USAGE_LIMIT_TIMES
      } else {
        requestData.buildingIds = formState.multipleBuildings
          ? formState.buildingIds.map((building) => building.id)
          : [building.id]
        requestData.notifyResidents = formState.notifyResidents
        requestData.isFeatured = formState.isFeatured
      }

      await api.createLoyaltyActivity(requestData)
      displaySnackbar(t('rewards-loyalty:activityForm.changesSavedSuccess'))
    } catch {
      displaySnackbar(t('rewards-loyalty:activityForm.changesSavedError'), true)
    } finally {
      onSubmitFinally()
    }
  }

  const handleFieldChange = ({ target: { name, value } }) => {
    setFormState((prev) => ({ ...prev, [name]: value }))
  }
  const handleCheckboxChange = getCheckboxHandler(handleFieldChange)
  const onHandleNameField = getNamedHandler(handleFieldChange)

  const handleNext = () => {
    setCurrentStep((prev) => prev + 1)
  }
  const handleBack = () => {
    if (!currentStep) {
      return onClose()
    }
    setCurrentStep((prev) => prev - 1)
  }

  const handleChangeAmount = (e) => {
    const max = MAX_AMOUNT_VALUE
    const min = 0
    const { value, name } = e.target
    let num = parseInt(value)
    if (num > max) num = max
    if (num < min) num = min
    return handleFieldChange({ target: { name, value: num } })
  }

  const rewardsActivityButtonEnabled = !!formState.activityName && !!formState.description
  const rewardsParametersButtonEnabled = !!formState.amount
  const rewardsAdvancedButtonEnabled =
    !isLoading &&
    (client || (!client && (!formState.multipleBuildings || formState.buildingIds.length > 0)))

  const steps = [
    {
      Component: RewardsActivityDetails,
      title: t('rewards-loyalty:activitySteps.activityDetails'),
      config: {
        canGoNext: rewardsActivityButtonEnabled,
        inputs: {
          first: {
            render: () => (
              <TextField name="name" value={client.name} style={{ width: '100%' }} disabled />
            ),
            label: t('rewards-loyalty:activityForm.residentLabel'),
            isVisible: !!client,
          },
          second: {
            render: () => (
              <React.Fragment>
                <TextField
                  name="activityName"
                  onChange={handleFieldChange}
                  value={formState.activityName}
                  placeholder={t('rewards-loyalty:activityForm.enterActivityNamePlaceholder')}
                  style={{ width: '100%' }}
                  minLength="1"
                  maxLength="30"
                />
                <span className="input-desc-label">
                  {t('rewards-loyalty:activityForm.maxLength30Info')}
                </span>
              </React.Fragment>
            ),
            label: t('rewards-loyalty:activityForm.activityNameLabel'),
          },
          third: {
            render: () => (
              <EditorText
                value={formState.description}
                onChange={(content, text) =>
                  onHandleNameField(text.trim() ? content : undefined, 'description')
                }
                placeholder={t('rewards-loyalty:activityForm.provideTaskDetailsPlaceholder')}
              />
            ),
            label: t('rewards-loyalty:activityForm.activityDescriptionLabel'),
          },
        },
      },
    },
    {
      Component: RewardsParameters,
      title: t('rewards-loyalty:activitySteps.parameters'),
      config: {
        canGoNext: rewardsParametersButtonEnabled,
        inputs: {
          first: {
            render: () => (
              <TextField
                name="amount"
                maxLength="10"
                onChange={handleChangeAmount}
                value={formState.amount}
                placeholder={t('rewards-loyalty:activityForm.enterAmountPlaceholder')}
                style={{ width: '100%' }}
                type="number"
                leftIcon={
                  <Icon
                    icon="dollar"
                    fill="none"
                    svgProps={{
                      stroke: '#3C3C3C',
                      strokeLinecap: 'round',
                      strokeLinejoin: 'round',
                    }}
                  />
                }
              />
            ),
            label: t('rewards-loyalty:activityForm.dollarValueLabel'),
          },
          second: {
            render: () => (
              <TextField
                name="amount"
                value={(formState.amount || 0) * POINTS_MULTIPLIER}
                style={{ width: '100%' }}
                disabled
              />
            ),
            label: t('rewards-loyalty:activityForm.pointsValueLabel'),
            disabled: true,
          },
          third: {
            render: () => (
              <TextField
                value={USAGE_LIMIT_TIMES}
                style={{ width: '100%' }}
                name="usage-limit"
                disabled
              />
            ),
            label: t('rewards-loyalty:activityForm.usageLimitLabel'),
            disabled: true,
          },
          fourth: {
            render: () => (
              <ToggleField
                label={t('rewards-loyalty:activityForm.requiresApprovalLabel')}
                labelOnRight
                name="requestApproval"
                onChange={() => onHandleNameField(!formState.requestApproval, 'requestApproval')}
                value={formState.requestApproval}
              />
            ),
          },
          fifth: {
            render: () => (
              <div className="radio-group-row">
                <RadioField
                  type="radio"
                  name="verificationMethod"
                  value={'image'}
                  label={t('rewards-loyalty:activityForm.photosScreenshotsLabel')}
                  onChange={(e) => onHandleNameField(e.target.value, 'verificationType')}
                  checked={formState.verificationType === 'image'}
                />
                <RadioField
                  type="radio"
                  name="verificationMethod"
                  value={'link'}
                  label={t('rewards-loyalty:activityForm.linkLabel')}
                  onChange={(e) => onHandleNameField(e.target.value, 'verificationType')}
                  checked={formState.verificationType === 'link'}
                />
                <RadioField
                  type="radio"
                  name="verificationMethod"
                  value={'text'}
                  label={t('rewards-loyalty:activityForm.textInputLabel')}
                  onChange={(e) => onHandleNameField(e.target.value, 'verificationType')}
                  checked={formState.verificationType === 'text'}
                />
                <RadioField
                  type="radio"
                  name="verificationMethod"
                  value={'none'}
                  label={t('rewards-loyalty:activityForm.noneLabel')}
                  onChange={(e) => onHandleNameField(e.target.value, 'verificationType')}
                  checked={formState.verificationType === 'none'}
                />
              </div>
            ),
            label: t('rewards-loyalty:activityForm.activityVerificationMethodLabel'),
            isVisible: formState.requestApproval,
          },
        },
      },
    },
    {
      Component: RewardsAdvanced,
      title: t('rewards-loyalty:activitySteps.advanced'),
      config: {
        canGoNext: rewardsAdvancedButtonEnabled,
        onSubmit,
        inputs: {
          first: {
            render: () => (
              <DateField
                name="expDate"
                placeholderText="(MM/DD/YYYY)"
                autoComplete="off"
                dateFormat="MM/dd/yyyy"
                minDate={new Date()}
                value={formState.expDate}
                onChange={handleFieldChange}
                onChangeRaw={(e) => e.preventDefault()}
              />
            ),
            label: (
              <div>
                {t('rewards-loyalty:activityForm.expirationDateLabel')}{' '}
                <span className="input-desc-label">
                  ({t('rewards-loyalty:activityForm.optionalSuffix')})
                </span>
              </div>
            ),
          },
          second: {
            render: () => (
              <React.Fragment>
                <CheckboxField
                  name={client ? 'notifyResident' : 'isFeatured'}
                  value={client ? formState.notifyResident : formState.isFeatured}
                  onChange={handleCheckboxChange}
                  label={
                    client ? (
                      t('rewards-loyalty:activityForm.notifyResidentsEmailLabel')
                    ) : (
                      <React.Fragment>
                        {t('rewards-loyalty:activityForm.makeActivityFeaturedLabel')}
                        <div data-tip data-for="pause-points-tip">
                          <Icon icon="info" viewBox="0 0 25 25" />
                          <ReactTooltip
                            className="tooltip"
                            id="pause-points-tip"
                            multiline
                            place="top"
                          >
                            {t('rewards-loyalty:activityForm.featuredActivityTooltip')}
                          </ReactTooltip>
                        </div>
                      </React.Fragment>
                    )
                  }
                />
                {!client && (
                  <React.Fragment>
                    <CheckboxField
                      name="notifyResidents"
                      onChange={handleCheckboxChange}
                      label={t('rewards-loyalty:activityForm.notifiedResidentsEmailLabel')}
                      value={formState.notifyResidents}
                    />
                    {isMultipleBuildings && (
                      <CheckboxField
                        name="multipleBuildings"
                        onChange={handleCheckboxChange}
                        label={t('rewards-loyalty:activityForm.addToMultipleBuildingsLabel')}
                        value={formState.multipleBuildings}
                      />
                    )}
                  </React.Fragment>
                )}
              </React.Fragment>
            ),
          },
          third: {
            render: () => (
              <Filter
                name="buildingIds"
                placeholder={cutTooLong(
                  formState.buildingIds && formState.buildingIds.length
                    ? formState.buildingIds.map((x) => (x.name ? x.name : x.text)).join(', ')
                    : t('rewards-loyalty:activityForm.buildingPlaceholder'),
                  50,
                )}
                options={[
                  {
                    id: 0,
                    text: t('rewards-loyalty:activityForm.allBuildings'),
                    name: t('rewards-loyalty:activityForm.allBuildings'),
                    selectAll: true,
                  },
                  ...buildings,
                ]}
                disabled={!buildings || buildings.length <= 1}
                value={formState.buildingIds}
                config={{ multiple: true, hasSearch: true }}
                onChange={handleFieldChange}
              />
            ),
            label: t('rewards-loyalty:activityForm.selectBuildingsLabel'),
            isVisible: !client && formState.multipleBuildings,
          },
        },
      },
    },
  ]

  const renderStep = () => {
    const { Component, config } = steps[currentStep]
    return <Component onNext={handleNext} onBack={handleBack} {...config} />
  }

  return (
    <ModalContainer
      additionalClass={'rewards-modal'}
      onClose={onClose}
      headerTitle={t('rewards-loyalty:activityForm.addLoyaltyActivityHeaderTitle')}
      headerIcon={<Icon icon="walk" viewBox="0 0 18 21" svgProps={{ fillRule: 'evenodd' }} />}
    >
      <div>
        <Stepper currentStep={currentStep} steps={steps.map((step) => step.title)} />
        {renderStep()}
      </div>
    </ModalContainer>
  )
}

RewardsAddLoyaltyActivityModal.propTypes = {
  onClose: PropTypes.func,
  fetchSingleBuilding: PropTypes.func,
  building: PropTypes.object,
  buildings: PropTypes.array,
  client: PropTypes.object,
}

export default connect(
  (state) => ({
    buildings: selectBuildingsForFilter(state),
  }),
  {
    fetchSingleBuilding,
  },
)(RewardsAddLoyaltyActivityModal)
